import { useCallback, useLayoutEffect, useRef, useState } from "react";

export interface SegmentedBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SegmentedIndicator {
  box: SegmentedBox;
  /**
   * Faux quand la géométrie a bougé sans changement d'onglet : le cadre doit
   * alors se replacer instantanément. L'animer donnerait l'impression qu'il
   * vole tout seul à chaque rotation d'écran ou champ qui apparaît.
   */
  animate: boolean;
}

const sameBox = (a: SegmentedBox | null, b: SegmentedBox): boolean =>
  a !== null && a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;

/**
 * Mesure l'onglet actif pour que le cadre soit animé en transform à l'intérieur
 * du conteneur, et non projeté en coordonnées écran comme le ferait `layoutId`.
 *
 * C'est ce qui le garde solidaire de son switch : lorsqu'un changement d'onglet
 * fait grandir le panneau — un champ qui apparaît, une liste qui s'allonge — le
 * conteneur se décale et emporte le cadre avec lui. Projeté, le cadre suivrait
 * une trajectoire calculée avant le décalage et se décrocherait en vol.
 */
export const useSegmentedIndicator = <T extends HTMLElement>(activeIndex: number) => {
  const [container, setContainer] = useState<T | null>(null);
  const [indicator, setIndicator] = useState<SegmentedIndicator | null>(null);
  const measuredOnce = useRef(false);

  // Une ref de rappel et non `useRef` : le switch peut n'apparaître qu'après le
  // montage — sur mobile il est replié derrière le premier temps de l'accueil —
  // et un effet ne se rejouerait pas à ce moment-là.
  const containerRef = useCallback((node: T | null) => setContainer(node), []);

  useLayoutEffect(() => {
    if (container === null) {
      measuredOnce.current = false;
      setIndicator(null);
      return;
    }

    const measure = (animate: boolean) => {
      const target = container.querySelectorAll<HTMLElement>("[role='tab']")[activeIndex];
      if (target === undefined) return;
      const next = {
        x: target.offsetLeft,
        y: target.offsetTop,
        width: target.offsetWidth,
        height: target.offsetHeight,
      };
      setIndicator((current) =>
        sameBox(current?.box ?? null, next) ? current : { box: next, animate },
      );
    };

    // Au tout premier placement il n'y a rien d'où glisser.
    measure(measuredOnce.current);
    measuredOnce.current = true;

    // Le switch peut changer de taille sans que l'onglet actif ne change :
    // rotation de l'écran, tiroir redimensionné, police chargée après coup.
    // Le garde d'égalité rend cet observateur muet lors d'un simple changement
    // d'onglet, où la mesure vient d'être faite juste au-dessus.
    const observer = new ResizeObserver(() => measure(false));
    observer.observe(container);
    for (const tab of container.querySelectorAll<HTMLElement>("[role='tab']")) {
      observer.observe(tab);
    }
    return () => observer.disconnect();
  }, [activeIndex, container]);

  return { containerRef, indicator };
};
